import React from 'react'
import {
  PLEXUS,
  THEME_BASE_WHITE,
  THEME_QVC_BLUE,
  PLEXUS_BLUE,
  THEME_PLEXUS_GREY,
  THEME_PLEXUS_BLUE,
  THEME_PLEXUS_GREEN,
  THEME_PLEXUS_LIGHT_BLUE,
} from 'theme/constants'

import { PLEXUS_PROJECT_ROUTE } from 'routes/constants'

import PlexusLogo from 'images/logos/plexus.svg'

// Plexus Images
import PlexusHeroLeft from 'images/projects/plexus/plexus-hero-left.webp'
import PlexusHeroRight from 'images/projects/plexus/plexus-hero-right.webp'
import PlexusFullWidthOne from 'images/projects/plexus/plexus-full-width-1.avif'
import PlexusFullWidthOneMobile from 'images/projects/plexus/plexus-full-width-1-mobile.webp'
import PlexusFullWidthTwo from 'images/projects/plexus/plexus-full-width-2.webp'
import PlexusFullWidthTwoMobile from 'images/projects/plexus/plexus-full-width-2-mobile.webp'
import PlexusFullWidthThree from 'images/projects/plexus/plexus-full-width-3.avif'
import PlexusFullWidthThreeMobile from 'images/projects/plexus/plexus-full-width-3-mobile.webp'
import PlexusFullWidthFour from 'images/projects/plexus/plexus-full-width-4.avif'
import PlexusFullWidthFourMobile from 'images/projects/plexus/plexus-full-width-4-mobile.webp'
import PlexusDesktopOne from 'images/projects/plexus/plexus-desktop-1.avif'
import PlexusDesktopOneMobile from 'images/projects/plexus/plexus-desktop-1-mobile.webp'
import PlexusMobileOne from 'images/projects/plexus/plexus-mobile-1.avif'
import PlexusMobileOneMobile from 'images/projects/plexus/plexus-mobile-1-mobile.webp'

const data = {
  id: PLEXUS,
  name: 'Plexus',
  route: PLEXUS_PROJECT_ROUTE,
  general: {
    logo: <PlexusLogo />,
    tagline: 'The power of live shopping',
  },
  theme: {
    primary: THEME_BASE_WHITE,
  },
  colors: {
    primary: PLEXUS_BLUE,
  },
  // Todo: make theme object and use primary and secondary instead of hardcoding theme variables
  hero: {
    theme: PLEXUS,
    logo: <PlexusLogo />,
    slides: [
      {
        text: 'Inclusive Business Intelligence',
        images: [
          { key: 'left', image: PlexusHeroLeft },
          { key: 'right', image: PlexusHeroRight },
        ],
      },
    ],
  },
  overview: {
    isClientStudio: false,
    theme: THEME_PLEXUS_GREY,
    titleTheme: THEME_PLEXUS_BLUE,
    client: 'Plexus',
    sector: 'Data & Insights',
    year: '2023',
    disciplines: 'Strategy, Product Design, Web',
    techStack: 'Python, Next.js, PostgreSQL, ElasticSearch',
    paragraphs: [
      'Plexus provides high-quality insights on emerging markets in multicultural ecosystems so that investors, researchers, and economic development agencies can tap into Black innovation opportunities.',
      'After spinning their wheels with an agency who presented a long waterfall roadmap, Plexus came to Studio eager to move fast and start testing with real users.',
    ],
  },
  solution: {
    theme: THEME_BASE_WHITE,
    title: 'The Solution',
    titleTheme: THEME_PLEXUS_GREEN,
    paragraphs: [
      'Our analysis from strategy sprints confirmed that it’s difficult to find current, trustworthy data about underrepresented businesses.',
      'Plexus needed the ease of usability that users were accustomed to on tools like Crunchbase and PitchBook, coupled with the unique datasets that Plexus could provide.',
    ],
    image: {
      source: PlexusDesktopOne,
      desktop: PlexusDesktopOne,
      mobile: PlexusDesktopOneMobile,
      alt: 'Plexus Search Result Dashboard',
    },
  },
  customSectionOne: {
    theme: THEME_PLEXUS_LIGHT_BLUE,
    title: 'UI Kit',
    titleTheme: THEME_QVC_BLUE,
    isImageTitle: true,
    image: {
      source: PlexusFullWidthOne,
      desktop: PlexusFullWidthOne,
      mobile: PlexusFullWidthOneMobile,
      alt: 'Plexus UI Kit',
    },
  },
  customSectionTwo: {
    theme: THEME_BASE_WHITE,
    title: 'The power of search',
    isImageTitle: true,
    isCentered: true,
    image: {
      source: PlexusFullWidthTwo,
      desktop: PlexusFullWidthTwo,
      mobile: PlexusFullWidthTwoMobile,
      alt: 'Plexus Search Bar & Filters UI components',
    },
    description: 'Fast and easy self-service search',
    paragraphs: [
      'This platform relies on a robust and flexible search and filter experience.',
      'Users can search within the major categories - Companies, Funds, People, Publications - and then further filter by founder ethnicity, gender, investment round, and so on.',
      'Save results to a list for future reference, or dig into a deeper details page for each result.',
    ],
  },
  customSectionThree: {
    theme: 'plexus-custom-gradient',
    image: {
      source: PlexusMobileOne,
      desktop: PlexusMobileOne,
      mobile: PlexusMobileOneMobile,
      alt: 'Plexus Dashboard on Mobile Devices',
    },
  },
  customSectionFour: {
    theme: THEME_PLEXUS_BLUE,
    description: 'Linked database properties',
    paragraphs: [
      'Clearly defined connections between data enable users to easily jump from a founder, to their company, and then to the related fund. ',
      'These searchable links create a better web of data and help users understand the relationship between objects for a deeper and more meaningful search experience.',
    ],
  },
  customSectionFive: {
    theme: THEME_BASE_WHITE,
    description: 'Subscribe to trends to track insights',
    paragraphs: [
      'Trending and suggested searches serve as a pathway to guide users toward relevant insights. Saved searches provide a means to notify users about updates that they care about, driving longterm retention.',
    ],
    isVertical: true,
    isCentered: true,
    image: {
      source: PlexusFullWidthThree,
      desktop: PlexusFullWidthThree,
      mobile: PlexusFullWidthThreeMobile,
      alt: 'Plexus Recent Searches & Top Search Result UI components',
    },
  },
  customSectionSix: {
    theme: THEME_BASE_WHITE,
    title: 'Light and dark modes',
    isCentered: true,
    image: {
      source: PlexusFullWidthFour,
      desktop: PlexusFullWidthFour,
      mobile: PlexusFullWidthFourMobile,
      alt: 'Plexus Light & Dark Mode UI feature',
    },
  },
}

export default data
